import React, { useState } from "react";
import swal from "sweetalert";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Tooltip,
  Divider,
  TextField,
  MenuItem,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FilterBar, ReportHeader, brand } from "./ReportsPages";
import { getSystemGrowthMetrics } from "../../services/reportService";
import { useTranslation } from "react-i18next";

export function SystemGrowthMetricsReport() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ from: "", to: "" });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      const data = await getSystemGrowthMetrics({
        start_date: filters.from,
        end_date: filters.to,
      });
      setReportData(data);
    } catch (err) {
      setError(err.message);
      swal(t("Error"), err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => swal(t("Info"), t("Export logic not yet implemented."), "info");

  // Use fetched data or a default structure
  const summary = reportData || {
    new_members: 0, member_exits: 0, loans_issued: 0, loans_repaid: 0, new_groups: 0, business_invoices: 0, donations: 0
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <ReportHeader
        title={t("System Growth Metrics")}
        subtitle={t("High level performance across all groups in the system.")}
        roleHint={t("Access: Admin / Desk Officer only")}
      />

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onGenerate={handleGenerate}
        onExport={handleExport}
        showGroup={false}
      />

      {loading && <Typography>{t("Loading report...")}</Typography>}
      {error && <Typography color="error">{t("Error")}: {error}</Typography>}

      {/* Summary metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t("New Members"), value: summary.new_members },
          { label: t("Member Exits"), value: summary.member_exits },
          { label: t("Loans Issued"), value: summary.loans_issued },
          { label: t("Loans Repaid"), value: summary.loans_repaid },
          { label: t("New Groups"), value: summary.new_groups },
          { label: t("Business Invoices"), value: summary.business_invoices },
        ].map((item) => (
          <Grid item xs={12} md={4} key={item.label}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {item.label}
                </Typography>
                <Typography variant="h6" fontWeight={900}>
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Simple chart-like mock */}
      {reportData && <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              {t("Visual Overview (Mock)")}
            </Typography>
            <Tooltip title={t("Replace with real chart component later (Recharts/Chart.js)")}>
              <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {[
              { label: t("Members"), value: summary.new_members },
              { label: t("Loans"), value: summary.loans_issued },
              { label: t("Groups"), value: summary.new_groups },
              { label: t("Donations"), value: summary.donations }, // Assuming donations are tracked
            ].map((item) => {
              const max = 50;
              const ratio = Math.min(item.value / max, 1);
              return (
                <Grid item xs={12} md={6} key={item.label}>
                  <Typography variant="body2" fontWeight={700}>
                    {item.label}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      width: "100%",
                      height: 14,
                      borderRadius: 999,
                      bgcolor: "#eee",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${ratio * 100}%`,
                        height: "100%",
                        bgcolor: brand,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {item.value} ({t("max scale")} {max})
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>}
    </Box>
  );
}
